/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion"; 
import {
  Typography, Button, IconButton, List, ListItem,
  ListItemPrefix, Drawer, Menu, MenuHandler, MenuList, MenuItem,
} from "@material-tailwind/react";
import {
  Bars3Icon, XMarkIcon, ChevronDownIcon, HomeIcon, 
  InformationCircleIcon, BriefcaseIcon, ChatBubbleLeftRightIcon, HeartIcon
} from "@heroicons/react/24/outline";

export default function Navbarsection() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false); 
  const location = useLocation();
  const currentPath = location.pathname;

  // --- 1. AUTO SCROLL TO TOP & SPINNER LOGIC ---
  useEffect(() => {
    // Start Spinner
    setLoading(true);
    
    // Instant Scroll to Top
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });

    // Close mobile drawer if open
    setIsDrawerOpen(false);

    // Fade out spinner after 800ms
    const timer = setTimeout(() => setLoading(false), 800); 
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);

  // Helper: Active link detection
  const isActive = (path) => (path === "/" ? currentPath === "/" || currentPath === "/home" : currentPath === path);

  const desktopActiveStyle = "bg-white text-black px-5 py-2 rounded-full shadow-md scale-105 transition-all duration-300 font-bold";
  const desktopNormalStyle = "text-black hover:text-white px-4 py-2 transition-all duration-300 font-bold uppercase text-[11px] tracking-widest";

  return (
    <>
      {/* --- 2. FAST LOADING SPINNER (Full Screen) --- */}
      <AnimatePresence mode="wait">
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-white/80 backdrop-blur-lg"
          >
             <div className="flex flex-col items-center gap-5">
                <motion.div
                  animate={{ 
                    rotate: 360,
                    scale: [1, 1.1, 1],
                    borderRadius: ["50%", "40%", "50%"] 
                  }}
                  transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
                  className="h-16 w-16 border-4 border-orange-100 border-t-orange-600 shadow-2xl"
                />
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-orange-900 font-black tracking-[0.2em] text-[10px] uppercase"
                >
                  Loading Experience
                </motion.p>
             </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* --- 3. ANIMATED NAV BAR --- */}
      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} // Cinematic easing
        className="sticky top-0 z-[100] w-full bg-orange-400 px-4 py-3 shadow-xl lg:px-8"
      >
        <div className="mx-auto flex max-w-screen-xl items-center justify-between text-black">
          
          {/* Logo with Hover effect */}
          <Link to="/" className="flex items-center gap-2 transform hover:rotate-2 transition-transform duration-300">
            <img 
              src="https://ik.imagekit.io/hickter/Hickter_Projects/MSWS/Logo/MSWS%20(6).png" 
              alt="Logo" 
              className="h-11 w-auto object-contain brightness-110" 
            />
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:block">
            <ul className="flex items-center gap-3">
              <li><Link to="/" className={isActive("/") ? desktopActiveStyle : desktopNormalStyle}>Home</Link></li>
              <li><Link to="/About" className={isActive("/About") ? desktopActiveStyle : desktopNormalStyle}>About</Link></li>
              <li><Link to="/Whyus" className={isActive("/Whyus") ? desktopActiveStyle : desktopNormalStyle}>Why Us?</Link></li>
              <li><Link to="/Servicesmain" className={isActive("/Servicesmain") ? desktopActiveStyle : desktopNormalStyle}>Services</Link></li>
              
              <Menu allowHover placement="bottom-start" offset={15}>
                <MenuHandler>
                  <li className={`flex cursor-pointer items-center gap-1 transition-all ${desktopNormalStyle}`}>
                    More <ChevronDownIcon className="h-4 w-4" strokeWidth={3} />
                  </li>
                </MenuHandler>
                <MenuList className="z-[110] border-none bg-orange-50 p-2 shadow-2xl rounded-2xl">
                  <MenuItem className="hover:bg-orange-100 rounded-xl transition-colors">
                    <Link to="/faq" className="block w-full py-1 text-black font-semibold">FAQ</Link>
                  </MenuItem>
                  <MenuItem className="hover:bg-orange-100 rounded-xl transition-colors">
                    <Link to="/Ourteams" className="block w-full py-1 text-black font-semibold">Our Teams</Link>
                  </MenuItem>
                  <MenuItem className="hover:bg-orange-100 rounded-xl transition-colors">
                    <Link to="/jobs" className="block w-full py-1 text-black font-semibold text-orange-700 font-black tracking-tight">Careers (Hiring)</Link>
                  </MenuItem>
                </MenuList>
              </Menu>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <div className="hidden lg:flex gap-3">
              <Link to="/Donation">
                <Button 
                  size="md" 
                  className="flex items-center gap-2 bg-white text-orange-700 rounded-full px-6 py-2.5 shadow-lg hover:shadow-2xl hover:-translate-y-1 active:scale-95 transition-all duration-300 font-black border-2 border-white"
                >
                  <HeartIcon className="h-5 w-5 text-red-500 animate-pulse" strokeWidth={3} /> 
                  DONATION
                </Button>
              </Link>
              <Link to="/Contact">
                <Button 
                  size="md" 
                  className="bg-black text-white hover:bg-gray-900 shadow-lg rounded-full px-7 transition-all border-2 border-black"
                >
                  Contact Us
                </Button>
              </Link>
            </div>

            {/* Mobile Burger Menu Icon */}
            <IconButton variant="text" className="lg:hidden" onClick={openDrawer}>
              <Bars3Icon className="h-10 w-10 text-black" strokeWidth={2} />
            </IconButton>
          </div>
        </div>
      </motion.nav>

      {/* --- 4. MOBILE FULLSCREEN DRAWER --- */}
      <Drawer
        placement="left"
        open={isDrawerOpen}
        onClose={closeDrawer}
        className="z-[9999] bg-orange-600 p-0" 
        size={window.innerWidth} 
      >
        <div className="flex h-screen flex-col overflow-hidden">
          {/* Mobile Header */}
          <div className="flex items-center justify-between border-b border-orange-500/50 p-6 backdrop-blur-md">
            <Typography variant="h5" color="white" className="font-black uppercase tracking-widest">
              MSWS Navigation
            </Typography>
            <IconButton variant="text" color="white" size="lg" onClick={closeDrawer}>
              <XMarkIcon className="h-10 w-10" strokeWidth={3} />
            </IconButton>
          </div>

          {/* Mobile Link List */}
          <List className="mt-8 px-6 space-y-2">
            {[
              { label: "Home", icon: <HomeIcon className="h-7 w-7" />, path: "/" },
              { label: "About Us", icon: <InformationCircleIcon className="h-7 w-7" />, path: "/About" },
              { label: "Our Services", icon: <BriefcaseIcon className="h-7 w-7" />, path: "/Servicesmain" },
              { label: "Contact Us", icon: <ChatBubbleLeftRightIcon className="h-7 w-7" />, path: "/Contact" },
            ].map((item) => (
              <Link to={item.path} key={item.label}>
                <ListItem 
                  className={`py-5 text-2xl font-bold rounded-2xl transition-all duration-300
                    ${isActive(item.path) 
                      ? "bg-white text-orange-600 shadow-2xl scale-[1.02]" 
                      : "text-white hover:bg-orange-800"}`}
                >
                  <ListItemPrefix className="mr-6">
                    {React.cloneElement(item.icon, {
                       className: `h-8 w-8 ${isActive(item.path) ? "text-orange-600" : "text-white"}`
                    })}
                  </ListItemPrefix>
                  {item.label}
                </ListItem>
              </Link>
            ))}
          </List>

          {/* Mobile CTA at Bottom */}
          <div className="mt-auto p-10 bg-orange-700/30">
            <Link to="/Donation">
              <Button 
                fullWidth 
                size="lg" 
                className="bg-white text-orange-700 shadow-2xl rounded-2xl text-2xl font-black flex items-center justify-center gap-4 py-6"
              >
                <HeartIcon className="h-8 w-8 text-red-500 animate-pulse" />
                DONATE NOW
              </Button>
            </Link>
          </div>
        </div>
      </Drawer>
    </>
  );
}